import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import { Observable, startWith, map, fromEvent, debounceTime, distinctUntilChanged, filter, Subject } from 'rxjs';
import { MsgModel } from '../../msg/model/msg.model';
import { Country } from '../model/country.model';
import { State } from '../model/state.model';
import { PostalAddressService } from '../service/postal-address.service';

@Component({
    selector: 'input-state-autocomplete',
    templateUrl: './input-state-autocomplete.component.html',
    styleUrls: ['./input-state-autocomplete.component.scss']
})
export class InputStateAutocompleteComponent implements OnInit {
    @Input() id: string = 'State';
    @Input() name: string = 'State';
    @Input() label: string = 'State';
    @Input() placeholder: string = 'State';
    @Input() required: boolean = false;
    @Input() disabledState: boolean = false;
    @Input() selectedState: State = { STATE_NAME: '' };
    @Output() selectedStateChange: EventEmitter<State> = new EventEmitter<State>();
    @Input() displayRequiredMessage: boolean = false;

    @Input() country: Country | undefined;
    @Output() countryChange: EventEmitter<Country> = new EventEmitter<Country>();

    @Input() countryId!: number;
    @Input() disabled: boolean;

    @ViewChild('searchString') searchString!: NgModel;
    @ViewChild('searchStringInput', { static: true }) searchStringInput!: ElementRef;
    searchStringChanged$: Observable<any> = new Subject<any>();

    isLoading: boolean = false;
    msg!: MsgModel;
    requiredMsg!: MsgModel;

    minCharacters = 3;

    states!: State[];

    constructor(private postalAddressService: PostalAddressService) {}

    ngOnInit(): void {
        this.requiredMsg = new MsgModel();
        this.requiredMsg.MSG = this.name +  ' is required';
        // Search by ID if given an ID
        if (this.selectedState.ID! > 0) {
            this.isLoading = true;
            this.searchStateById(this.selectedState.ID).subscribe((res: any) => {
                this.isLoading = false;
                if (res['state'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    this.onSelect(res['state']);
                }
            });
        }
        // Search by ID if CITY_NAME is a number
        else if (this.selectedState.STATE_NAME && !isNaN(Number(this.selectedState.STATE_NAME))) {
            this.isLoading = true;
            this.searchStateById(Number(this.selectedState.STATE_NAME)).subscribe((res: any) => {
                this.isLoading = false;
                if (res['state'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    this.onSelect(res['state']);
                }
            });
        }
        // Search by CITY_NAME w/ no ID
        else if (this.selectedState.STATE_NAME && this.selectedState.STATE_NAME.length > this.minCharacters) {
            this.isLoading = true;
            this.searchState(this.selectedState.STATE_NAME).subscribe((res: any) => {
                this.isLoading = false;
                if (res['states'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    if (res.states.length === 1) {
                        this.onSelect(res['states']);
                    } else {
                        let found: boolean = false;
                        for (var i = 0; i < res.states.length; i++) {
                            if (
                                res.states[i].STATE?.COUNTRy?.COUNTRY_NAME === this.selectedState?.COUNTRy?.COUNTRY_NAME ||
                                res.states[i].STATE?.COUNTRy?.COUNTRY_NAME === this.country?.COUNTRY_NAME
                            ) {
                                this.onSelect(res.states[i]);
                                found = true;
                                break;
                            }
                        }

                        if (!found) {
                            for (var i = 0; i < res.states.length; i++) {
                                if (res.states[i].CITY_NAME === this.selectedState.STATE_NAME) {
                                    this.onSelect(res.states[i]);
                                    found = true;
                                    break;
                                }
                            }
                        }
                    }
                }
            });
        }

        fromEvent(this.searchStringInput.nativeElement, 'input')
            .pipe(
                // get value
                map((event: any) => {
                    return event.target.value;
                }),
                // only search when value has changed
                distinctUntilChanged(),
                // 500 ms delay before searching
                debounceTime(500),
                // only search if search string is >= 3 characters
                filter((res) => {
                    let canSearch = res !== null && res.length >= this.minCharacters;

                    let newState = new State();
                    newState.STATE_NAME = res;
                    this.onSelect(newState);

                    return canSearch;
                })
            )
            .subscribe((text: string) => {
                this.isLoading = true;

                this.searchState(text).subscribe((res: any) => {
                    this.isLoading = false;
                    if (res['states'] == undefined) {
                        let newState = new State();
                        newState.STATE_NAME = text;
                        this.onSelect(newState);
                        this.states = [];
                    } else {
                        this.states = res['states'];
                    }
                });
            });
    }

    autoSelect(states?: State[]) {
        if (!states) {
            return;
        } else if (states.length === 0) {
            return;
        } else if (states.length === 1) {
            for (var i = 0; i < states.length; i++) {
                if (states[i].STATE_NAME === this.selectedState.STATE_NAME) {
                    this.onSelect(states[i]);
                    break;
                }
            }
        }
    }

    searchState(searchString: any): Observable<State[]> {
        return this.postalAddressService.searchStates(searchString, this.countryId);
    }

    searchStateById(id: any): Observable<State[]> {
        return this.postalAddressService.searchStatesById(id);
    }

    onSelect(state: State) {
        this.selectedState = state;
        this.selectedStateChange.emit(this.selectedState);

        if (this.selectedState.COUNTRy) {
            this.country = JSON.parse(JSON.stringify(this.selectedState.COUNTRy));
            this.countryChange.emit(this.country);
        }

        this.searchString.control.setValue(this.selectedState.STATE_NAME);
        this.checkForId(this.searchString);

        this.clearAutocompleteArray();
    }

    clearAutocompleteArray() {
        this.states = [];
    }

    checkForId(input: NgModel) {
        if (this.selectedState.STATE_NAME && this.selectedState.STATE_NAME.length === 0) {
            input.control.setErrors({ Required: 'New State will be created'});
        }
        if (!this.selectedState.ID) {
            input.control.setErrors({ idRequired: 'New State will be created'});
        } else {
            input.control.setErrors(null);
        }
    }
}
